import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  Grid,
  Checkbox,
} from "@mui/material";
import { useUserContext } from "./context/UserContext";
import { db } from "../firebase.config";

export default function SelectCreditNotesModal({ open, onClose, onConfirm }) {
  const { userId } = useUserContext();
  const [allCreditNotes, setAllCreditNotes] = useState([]);
  const [selectedCreditNotes, setSelectedCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState("open");

  useEffect(() => {
    if (!open) return;

    const fetchCreditNotes = async () => {
      setLoading(true);
      try {
        if (!userId) {
          console.error("User ID is not available");
          setLoading(false);
          return;
        }

        const creditNotesRef = collection(db, "users", userId, "creditNotes");
        const creditNotesSnapshot = await getDocs(creditNotesRef);
        const creditNotesList = creditNotesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllCreditNotes(creditNotesList);
      } catch (error) {
        console.error("Error fetching credit notes:", error);
        setSnackbar({
          open: true,
          message: "Error fetching credit notes: " + error.message,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCreditNotes();
  }, [open, userId]);

  // Filter credit notes based on search and filters
  const filteredCreditNotes = allCreditNotes.filter((cn) => {
    const matchesSearch =
      cn.creditNoteNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
      cn.billTo?.name?.toLowerCase().includes(searchText.toLowerCase());

    const status = cn.status || "open";
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "open" && status === "open") ||
      (filterStatus === "closed" && status === "closed");

    return matchesSearch && matchesStatus;
  });

  // Filter to only show OPEN credit notes for selection (exclude closed ones)
  const selectableCreditNotes = filteredCreditNotes.filter((cn) => {
    const status = cn.status || "open";
    return status === "open";
  });

  const handleSelectCreditNote = (creditNote) => {
    // Prevent selection of closed credit notes
    const status = creditNote.status || "open";
    if (status === "closed") {
      setSnackbar({
        open: true,
        message: "Closed credit notes cannot be selected",
      });
      return;
    }

    const isAlreadySelected = selectedCreditNotes.some((cn) => cn.id === creditNote.id);
    if (isAlreadySelected) {
      setSelectedCreditNotes(
        selectedCreditNotes.filter((cn) => cn.id !== creditNote.id)
      );
    } else {
      setSelectedCreditNotes([...selectedCreditNotes, creditNote]);
    }
  };

  const handleConfirmSelection = () => {
    if (selectedCreditNotes.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one credit note",
      });
      return;
    }

    onConfirm(selectedCreditNotes);
    handleClose();
  };

  const handleClose = () => {
    setSelectedCreditNotes([]);
    setSearchText("");
    setFilterStatus("all");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", color: "#1976d2" }}>
        Select Credit Notes
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Search and Filter */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Search by credit note # or customer name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
              />

              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            {/* Credit Notes List */}
            <Grid item xs={12}>
              <Paper sx={{ maxHeight: "400px", overflow: "auto" }}>
                {filteredCreditNotes.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: "center", color: "#999" }}>
                    <Typography variant="body2">
                      No credit notes found.
                    </Typography>
                  </Box>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell sx={{ width: "50px" }}>
                          <Checkbox disabled />
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Credit Note #</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCreditNotes.map((creditNote) => {
                        const status = creditNote.status || "open";
                        const isClosed = status === "closed";
                        
                        return (
                          <TableRow
                            key={creditNote.id}
                            sx={{
                              backgroundColor: isClosed 
                                ? "#f5f5f5" 
                                : selectedCreditNotes.some(
                                    (cn) => cn.id === creditNote.id
                                  )
                                ? "#e3f2fd"
                                : "transparent",
                              cursor: isClosed ? "not-allowed" : "pointer",
                              opacity: isClosed ? 0.6 : 1,
                              "&:hover": {
                                backgroundColor: isClosed 
                                  ? "#f5f5f5"
                                  : selectedCreditNotes.some(
                                      (cn) => cn.id === creditNote.id
                                    )
                                  ? "#e3f2fd"
                                  : "#f9f9f9",
                              },
                            }}
                            onClick={() => !isClosed && handleSelectCreditNote(creditNote)}
                          >
                            <TableCell>
                              <Checkbox
                                disabled={isClosed}
                                checked={selectedCreditNotes.some(
                                  (cn) => cn.id === creditNote.id
                                )}
                                onChange={() => !isClosed && handleSelectCreditNote(creditNote)}
                              />
                            </TableCell>
                            <TableCell>{creditNote.creditNoteNumber}</TableCell>
                            <TableCell>{creditNote.billTo?.name || "-"}</TableCell>
                            <TableCell>{creditNote.creditNoteDate || "-"}</TableCell>
                            <TableCell>
                              <span style={{
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                backgroundColor: isClosed ? "#ffcdd2" : "#c8e6c9",
                                color: isClosed ? "#c62828" : "#2e7d32"
                              }}>
                                {status.toUpperCase()}
                              </span>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: "600" }}>
                              ₹{(creditNote.totals?.grandTotal || 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Grid>

            {/* Selected Summary */}
            {selectedCreditNotes.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: "#f0f7ff", border: "1px solid #b3e5fc" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                    Selected: {selectedCreditNotes.length} Credit Note(s)
                  </Typography>
                  <Box>
                    {selectedCreditNotes.map((cn) => (
                      <Box
                        key={cn.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 0.5,
                          fontSize: "0.9rem",
                        }}
                      >
                        <span>{cn.creditNoteNumber}</span>
                        <span sx={{ fontWeight: "600" }}>
                          ₹{(cn.totals?.grandTotal || 0).toFixed(2)}
                        </span>
                      </Box>
                    ))}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pt: 1,
                      mt: 1,
                      borderTop: "1px solid #b3e5fc",
                      fontWeight: "bold",
                    }}
                  >
                    <span>Total:</span>
                    <span>
                      ₹
                      {selectedCreditNotes
                        .reduce((sum, cn) => sum + (cn.totals?.grandTotal || 0), 0)
                        .toFixed(2)}
                    </span>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          disabled={selectedCreditNotes.length === 0}
        >
          Confirm Selection ({selectedCreditNotes.length})
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Dialog>
  );
}
